import { motion } from "framer-motion";
import Container from "./Container";
import { NavLink } from "react-router-dom";
import { LiveLeadEngine } from "./Infographic";

const easeOut = { duration: 0.35, ease: [0.25, 0.1, 0.25, 1] };

const Hero = () => {
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 via-slate-50/80 to-white py-10 sm:py-12 lg:py-14 xl:py-16">
      <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden>
        <div className="absolute left-0 top-0 h-[360px] w-[480px] -translate-y-1/4 rounded-full bg-emerald-100/30 blur-3xl" />
        <div className="absolute right-0 top-1/4 h-64 w-96 rounded-full bg-slate-100/40 blur-2xl" />
      </div>

      <Container className="relative py-6 lg:py-8">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-12 xl:gap-16">
          {/* Left: subtle fade + translate on load */}
          <motion.div
            className="flex flex-col text-left"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={easeOut}
          >
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600 sm:text-sm">
                FOR FREELANCERS
              </p>
              <h1 className="max-w-xl text-3xl font-bold leading-[1.15] tracking-tight text-slate-900 sm:text-4xl lg:text-[2.5rem] xl:text-5xl">
                Stop bidding. Start responding.
              </h1>
              <p className="mt-3 max-w-lg text-base leading-relaxed text-slate-600 sm:text-lg lg:text-xl">
                Your next client is on Reddit and LinkedIn asking for help—not
                posting job listings. We surface those conversations so you show
                up first.
              </p>
              <p className="mt-1.5 text-sm font-medium text-slate-500">
                No proposals. No race to the bottom. Just you and the right thread.
              </p>

              {/* CTA block */}
              <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-4">
                <NavLink
                  to="/signup"
                  className="inline-flex items-center justify-center rounded-xl bg-emerald-600 px-7 py-4 text-lg font-semibold text-white shadow-xl shadow-emerald-600/30 transition hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
                >
                  Get started free
                </NavLink>
                <a
                  href="#demo"
                  className="inline-flex items-center justify-center rounded-xl border-2 border-slate-200 bg-white px-6 py-3.5 text-base font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-2"
                >
                  See how it works
                </a>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                <span>No credit card · 2-min setup</span>
                <span className="font-medium text-emerald-700">
                  Used by 400+ freelancers
                </span>
              </div>

              {/* Pattern interrupt: live client opportunities */}
              <div className="mt-6 flex items-center gap-3">
                <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                  </span>
                  <span className="text-xs font-medium text-slate-600">
                    2 new client opportunities · Reddit & LinkedIn
                  </span>
                </div>
              </div>
            </motion.div>

          {/* Right: product-style live feed mockup */}
          <motion.div
            className="hidden lg:flex lg:items-center lg:justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...easeOut, delay: 0.1 }}
          >
            <LiveLeadEngine />
          </motion.div>
        </div>
      </Container>

      {/* Soft transition into next section */}
      <div
        className="h-px w-full bg-gradient-to-r from-transparent via-slate-200 to-transparent"
        aria-hidden
      />
    </section>
  );
};

export default Hero;
